import { FC, useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  CircularProgress,
  Box,
  Typography
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { getAuthenticatedApiClient } from "../../../api/apiClient";
import { Signatory, UserDto } from "../../../api/Api";

type Props = {
  open: boolean;
  onClose: () => void;
  onAdd: (signatory: Signatory) => void;
  existingEmails: string[];
};

const useDebounced = <T,>(value: T, ms: number) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
};

export const AddVpDialog: FC<Props> = ({
  open,
  onClose,
  onAdd,
  existingEmails
}) => {
  const [query, setQuery] = useState("");
  const [selectedUser, setSelectedUser] = useState<UserDto | null>(null);
  const debouncedQuery = useDebounced(query, 300);

  useEffect(() => {
    if (!open) {
      setQuery("");
      setSelectedUser(null);
    }
  }, [open]);

  const { data, isFetching } = useQuery({
    queryKey: ["signoff-user-search", debouncedQuery],
    enabled: open && debouncedQuery.trim().length >= 2,
    queryFn: async () => {
      const apiClient = await getAuthenticatedApiClient();
      const response = await apiClient.userSearch.userSearch({
        q: debouncedQuery.trim(),
        top: 20
      });
      return response.data;
    }
  });

  const existingSet = useMemo(
    () => new Set(existingEmails.map((e) => e.toLowerCase())),
    [existingEmails]
  );

  const handleSelect = (user: UserDto) => {
    if (!user.email) return;
    if (existingSet.has(user.email.toLowerCase())) return;
    setSelectedUser(user);
  };

  const handleConfirm = () => {
    if (!selectedUser?.email) return;
    onAdd({
      name: selectedUser.name || selectedUser.email,
      email: selectedUser.email,
      order: 0,
      isCeo: false,
      signed: false
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Add VP signatory</DialogTitle>
      <DialogContent dividers>
        <TextField
          autoFocus
          fullWidth
          label="Search by name or email"
          placeholder="At least 2 characters"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          sx={{ marginBottom: 2 }}
        />
        {isFetching && (
          <Box sx={{ display: "flex", justifyContent: "center", padding: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
        {!isFetching &&
          debouncedQuery.trim().length >= 2 &&
          data &&
          data.length === 0 && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ padding: 1 }}
            >
              No users matched.
            </Typography>
          )}
        <List dense>
          {(data || []).map((user) => {
            const isExisting = user.email
              ? existingSet.has(user.email.toLowerCase())
              : true;
            const isSelected =
              !!selectedUser?.email &&
              !!user.email &&
              selectedUser.email.toLowerCase() === user.email.toLowerCase();
            return (
              <ListItem key={user.id || user.email} disablePadding>
                <ListItemButton
                  onClick={() => handleSelect(user)}
                  disabled={isExisting || !user.email}
                  selected={isSelected}
                >
                  <ListItemText
                    primary={user.name || user.email}
                    secondary={[user.email, user.department]
                      .filter(Boolean)
                      .join(" · ")}
                  />
                  {isExisting && (
                    <Typography variant="caption" color="text.secondary">
                      Already added
                    </Typography>
                  )}
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={!selectedUser}
        >
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
};
